import type { GameMap, Building } from '../shared/map';
import SPRITES from './sprites.json';
import { INK, FONT } from './ui';

// The ground in DHARNA 144's inks (docs/plans/2026-10-18-dharna-144.md, section 8, option B): paper paving, warm road
// tar with newsprint paint, the restricted ground behind the walls hatched as on the operation map, parks screened in
// halftone, the square in riso red. Buildings, trees and people keep their colours. P is the protest road (Jantar
// Mantar Road on the OSM map), M the yantras (Jantar Mantar plaster). B is only seen on maps without polygons.
export const COLORS: Record<string, string> = {
  '.': '#DDCEB0', '=': '#35332F', p: '#E6D9BE', P: '#D9483F', G: INK.metro, B: '#C98A78', W: '#D6C7A8', M: '#E07A5A',
};
export const TEAM_COLOR = ['#F8C40A', '#0A24B0', '#B5A890']; // protester (--metro), police (--barricade), npc (--newsprint-dim)

/** Colours for the OSM point markers, by poi group (see the spec's pois.groups). */
export const POI_COLOR: Record<string, string> = {
  gate: '#F2C230', barrier: '#9A948A', metro: '#E0457B', bus: '#4FA3E0', rail: '#7D8597', police: '#3D6FD9',
  government: '#B98CF0', toilet: '#5CC8B4', water: '#6FD3F2', heritage: '#E8935A', leisure: '#8BC34A', place: '#F1ECE0', other: '#FFFFFF',
};

// road drawing colours: paper pavements a shade over the ground, newsprint kerbs, the tar of the '=' tiles, newsprint
// paint; the square riso red, hatched
const PAVEMENT = '#E9DCC1', KERB = INK.newsprint, ASPHALT = COLORS['='], PAINT = 'rgba(236,224,200,.9)', ZEBRA = 'rgba(236,224,200,.95)', ZONE = 'rgba(227,52,47,.38)', ZONE_HATCH = 'rgba(227,52,47,.55)';

/** Road geometry in world px with bounding boxes, so a chunk only strokes what crosses it. */
export interface RoadDraw {
  roads: { c: string; w: number; o: boolean; pts: number[]; bb: [number, number, number, number] }[];
  junctions: [number, number, number][];
  crossings: { pts: number[]; bb: [number, number, number, number] }[];
  zone: number[][];
}

export function prepareRoads(m: GameMap): RoadDraw | null {
  if (!m.roadLines || !m.markings) return null;
  const t = m.tile, metre = t / (m.mpt || 1);
  const bbox = (pts: number[], pad: number): [number, number, number, number] => {
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (let i = 0; i < pts.length; i += 2) { x0 = Math.min(x0, pts[i]); x1 = Math.max(x1, pts[i]); y0 = Math.min(y0, pts[i + 1]); y1 = Math.max(y1, pts[i + 1]); }
    return [x0 - pad, y0 - pad, x1 + pad, y1 + pad];
  };
  const kerb = m.markings.kerbM;
  return {
    roads: m.roadLines.map((r) => { const pts = r.l.map((v) => v * t); return { c: r.c, w: r.w * metre, o: !!r.o, pts, bb: bbox(pts, (r.w / 2 + kerb + 1) * metre) }; }),
    junctions: (m.junctions || []).map(([x, y, rr]) => [x * t, y * t, rr * t]),
    crossings: (m.crossings || []).map((l) => { const pts = l.map((v) => v * t); return { pts, bb: bbox(pts, m.markings!.zebra.widthM * metre) }; }),
    zone: (m.zone || []).map((poly) => poly.flatMap(([x, y]) => [x * t, y * t])),
  };
}

let discScratch: HTMLCanvasElement | null = null;
const rgbOf = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
/**
 * Walled compounds (docs/plans/2026-10-18-no-go.md, 2026-10-18-street-geometry.md): on a walled map the ground starts
 * as lawn, the plots' gardens, and the roads paint their right of way over it (carriageway, kerbs, pavement), so the
 * floor turns from paving to lawn exactly on the wall line. Open ground that is not a road is paved ('pave'); a clipped
 * hedge runs inside each wall.
 */
const LAWN = '#D6C7A8', HEDGE = 'rgba(14,12,9,.6)'; // the compounds' ground, and the ink rule just inside each wall
const GROUND_VARY: Record<string, [number, number, number]> = {
  '.': rgbOf(COLORS['.']), p: rgbOf(COLORS.p), pave: rgbOf(PAVEMENT), lawn: rgbOf(LAWN),
};
/** The operation map's textures on the street, made once: hatching for restricted ground, a halftone for parks. In
 *  world px, so a pattern runs on unbroken from chunk to chunk. */
const textures: { hatch?: CanvasPattern; dots?: CanvasPattern } = {};
function texture(g: CanvasRenderingContext2D, kind: 'hatch' | 'dots'): CanvasPattern {
  const have = textures[kind];
  if (have) return have;
  const c = document.createElement('canvas'), n = kind === 'hatch' ? 10 : 9;
  c.width = c.height = n;
  const h = c.getContext('2d')!;
  if (kind === 'hatch') { h.strokeStyle = 'rgba(14,12,9,.2)'; h.lineWidth = 1.4; h.beginPath(); for (const o of [-n, 0, n]) { h.moveTo(o, n); h.lineTo(o + n, 0); } h.stroke(); }
  else { h.fillStyle = 'rgba(14,12,9,.2)'; h.beginPath(); h.arc(n / 2, n / 2, 1.3, 0, Math.PI * 2); h.fill(); }
  return (textures[kind] = g.createPattern(c, 'repeat')!);
}

/** A tile shade from its position, so paving slabs and lawn patches don't repeat. */
const vary = (x: number, y: number) => 0.991 + ((((x * 73856093) ^ (y * 19349663)) >>> 0) % 1000) / 1000 * 0.018; // on paper a wider swing reads as a checkerboard
export const enclosed = (m: GameMap, x: number, y: number) => m.rows[y][x] === 'W' || (!!m.nogo && m.nogo[y * m.w + x] === 1);

/** Roads as smooth strips along their OSM centrelines, over the rectangle (rx0, ry0)-(rx1, ry1) in world px. */
function paintRoads(g: CanvasRenderingContext2D, m: GameMap, R: RoadDraw, rx0: number, ry0: number, rx1: number, ry1: number) {
  const M = m.markings!, metre = m.tile / (m.mpt || 1);
  const hit = (bb: number[]) => bb[0] < rx1 && bb[2] > rx0 && bb[1] < ry1 && bb[3] > ry0;
  const roads = R.roads.filter((r) => hit(r.bb));
  const path = (pts: number[]) => { g.beginPath(); g.moveTo(pts[0], pts[1]); for (let i = 2; i < pts.length; i += 2) g.lineTo(pts[i], pts[i + 1]); };
  const stroke = (list: typeof roads, width: (r: (typeof roads)[0]) => number, style: string) => {
    g.strokeStyle = style;
    for (const r of list) { const w = width(r); if (w <= 0) continue; g.lineWidth = w; path(r.pts); g.stroke(); }
  };
  g.save(); g.lineCap = 'round'; g.lineJoin = 'round';
  // layered over every road at once, so a crossing road's asphalt covers the other's kerb and edge lines
  const pave = M.pavementM || 0;
  if (pave) stroke(roads, (r) => r.w + 2 * (M.kerbM + pave) * metre, PAVEMENT);
  stroke(roads, (r) => r.w + 2 * M.kerbM * metre, KERB);
  stroke(roads, (r) => r.w, ASPHALT);
  const edged = roads.filter((r) => M.edgeClasses.includes(r.c) && r.w > 2 * (M.edgeInsetM + M.edgeLineM) * metre + 2 * metre);
  stroke(edged, (r) => r.w - 2 * M.edgeInsetM * metre, PAINT);
  stroke(edged, (r) => r.w - 2 * (M.edgeInsetM + M.edgeLineM) * metre, ASPHALT);
  // centre dashes on wide two-way roads, broken at junctions by asphalt discs
  g.setLineDash(M.centreDashM.map((v) => v * metre)); g.lineCap = 'butt';
  stroke(roads.filter((r) => !r.o && M.centreClasses.includes(r.c) && r.w >= M.centreMinWidthM * metre), () => M.centreLineM * metre, PAINT);
  g.setLineDash([]); g.lineCap = 'round';
  // junction discs clear the paint off the middle of a junction; they are cut to the asphalt, so they never spill
  // over the kerb onto the ground (on a scratch canvas: discs kept only where the asphalt mask is)
  const discs = R.junctions.filter(([x, y, r]) => x + r > rx0 && x - r < rx1 && y + r > ry0 && y - r < ry1);
  if (discs.length) {
    const w = Math.ceil(rx1 - rx0), h = Math.ceil(ry1 - ry0);
    const c = discScratch && discScratch.width === w && discScratch.height === h ? discScratch : (discScratch = document.createElement('canvas'));
    c.width = w; c.height = h;
    const s = c.getContext('2d')!;
    s.translate(-rx0, -ry0); s.lineCap = 'round'; s.lineJoin = 'round'; s.strokeStyle = '#000';
    for (const r of roads) { s.lineWidth = r.w; s.beginPath(); s.moveTo(r.pts[0], r.pts[1]); for (let i = 2; i < r.pts.length; i += 2) s.lineTo(r.pts[i], r.pts[i + 1]); s.stroke(); }
    s.globalCompositeOperation = 'source-in'; s.fillStyle = ASPHALT;
    for (const [x, y, r] of discs) { s.beginPath(); s.arc(x, y, r, 0, Math.PI * 2); s.fill(); }
    g.drawImage(c, rx0, ry0);
  }
  // the protest zone as a riso tint over its stretch of road, hatched, edged in riso
  for (const poly of R.zone) {
    path(poly); g.closePath(); g.fillStyle = ZONE; g.fill();
    g.save(); g.clip(); g.strokeStyle = ZONE_HATCH; g.lineWidth = 0.35 * metre; g.beginPath();
    for (let k = rx0 - (ry1 - ry0); k < rx1; k += 1.6 * metre) { g.moveTo(k, ry1); g.lineTo(k + (ry1 - ry0), ry0); }
    g.stroke(); g.restore();
    path(poly); g.closePath(); g.strokeStyle = INK.riso; g.lineWidth = 0.4 * metre; g.stroke();
  }
  // zebras: bars across the crossing path, so they run along the road
  g.setLineDash([M.zebra.barM * metre, M.zebra.gapM * metre]); g.lineWidth = M.zebra.widthM * metre; g.strokeStyle = ZEBRA;
  for (const c of R.crossings) if (hit(c.bb)) { path(c.pts); g.stroke(); }
  g.restore();
}

/**
 * Paint the tiles x0..x1, y0..y1 (exclusive ends) of the map into g, in world px. With road vectors, road and zone
 * tiles start as plain ground, the roads are drawn as strips, and then the solid tiles go back on top, since a building
 * or wall that overlaps a road wins in the tile grid too.
 */
function paintTiles(g: CanvasRenderingContext2D, m: GameMap, x0: number, y0: number, x1: number, y1: number, R: RoadDraw | null, BL: BuildingLayer | null) {
  const t = m.tile;
  for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) {
    const ch = m.rows[y][x];
    const plain = (R && (ch === '=' || ch === 'P')) || (BL && (ch === 'B' || ch === 'M'));
    const c = plain ? '.' : ch;
    // paving slabs and lawn patches: each tile a shade lighter or darker, from a hash of its position. On a walled map
    // plain ground is lawn unless it is paved open ground; the roads then paint their right of way over it. A wall tile
    // ('W') is lawn on any map: its wall stands on its edge (compounds.ts)
    const walled = !!m.walls.length, i = y * m.w + x;
    const kind = c === 'W' ? 'lawn' : c === '.' && walled ? (m.paved?.[i] ? 'pave' : 'lawn') : c, v = GROUND_VARY[kind];
    if (v) g.fillStyle = css(v, vary(x, y));
    else g.fillStyle = COLORS[c] || '#f0f';
    g.fillRect(x * t, y * t, t, t);
    // restricted ground hatched, parks screened: the same marks the operation map uses
    if (kind === 'lawn' || kind === 'p') { g.fillStyle = texture(g, kind === 'lawn' ? 'hatch' : 'dots'); g.fillRect(x * t, y * t, t, t); }
  }
  if (R) paintRoads(g, m, R, x0 * t, y0 * t, x1 * t, y1 * t);
  // rounded block corners: the sliver between the arc and the old corner is pavement, like the rest of the street
  if (m.corners.length) {
    g.fillStyle = PAVEMENT;
    for (const c of m.corners) { g.beginPath(); g.moveTo(c[0], c[1]); for (let i = 2; i < c.length; i += 2) g.lineTo(c[i], c[i + 1]); g.closePath(); g.fill(); }
  }
  paintHedges(g, m, x0 * t, y0 * t, x1 * t, y1 * t);
  if (BL) BL.paintShadows(g, x0 * t, y0 * t, x1 * t, y1 * t);
  const open = (x: number, y: number) => x >= 0 && y >= 0 && x < m.w && y < m.h && !m.solid[y * m.w + x];
  const isP = (x: number, y: number) => x >= 0 && y >= 0 && x < m.w && y < m.h && m.rows[y][x] === 'P';
  // building roofs get a lighter top and a darker south face; walls a thin edge, so shapes read at a glance
  for (let y = y0; y < y1; y++) for (let x = x0; x < x1; x++) {
    const ch = m.rows[y][x];
    if (BL && (ch === 'B' || ch === 'M')) continue; // drawn standing up by BuildingLayer
    if (R && (ch === 'B' || ch === 'M')) { g.fillStyle = COLORS[ch]; g.fillRect(x * t, y * t, t, t); }
    if (ch === 'B') {
      g.fillStyle = '#8A3B2B'; g.fillRect(x * t + 2, y * t + 2, t - 4, t - 4);
      if (open(x, y + 1)) { g.fillStyle = '#4E2018'; g.fillRect(x * t, y * t + t - 8, t, 8); }
    } else if (ch === 'G') {
      // an OSM gate (a compound's or a lane's): walkable, so drawn open, paving between two ink gateposts on its
      // corners. Full hazard stripes read as a wall you cannot pass, and these are not one
      g.fillStyle = PAVEMENT; g.fillRect(x * t, y * t, t, t);
      const post = Math.max(4, t * 0.16);
      g.fillStyle = INK.asphalt; g.fillRect(x * t + 1, y * t + 1, post, post); g.fillRect(x * t + t - post - 1, y * t + t - post - 1, post, post);
      g.fillStyle = INK.metro; g.fillRect(x * t + 2, y * t + 2, post - 2, (post - 2) / 2); g.fillRect(x * t + t - post, y * t + t - post, post - 2, (post - 2) / 2);
    } else if (ch === 'M') {
      g.fillStyle = '#D9805F'; g.fillRect(x * t + 3, y * t + 3, t - 6, t - 6);
      if (open(x, y + 1)) { g.fillStyle = '#8A3A26'; g.fillRect(x * t, y * t + t - 8, t, 8); }
    } else if (!R && ch === 'P' && (!isP(x - 1, y) || !isP(x + 1, y))) {
      // kerb lines along the protest road so it reads as a street, not a blob (the road strips draw their own)
      g.fillStyle = 'rgba(236,224,200,.5)';
      if (!isP(x - 1, y)) g.fillRect(x * t, y * t, 3, t);
      if (!isP(x + 1, y)) g.fillRect(x * t + t - 3, y * t, 3, t);
    }
  }
}

/** A clipped hedge just inside every boundary wall in the rectangle (world px), along the wall at whatever angle. */
function paintHedges(g: CanvasRenderingContext2D, m: GameMap, rx0: number, ry0: number, rx1: number, ry1: number) {
  if (!m.walls.length) return;
  const metre = m.tile / (m.mpt || 1), off = 1.1 * metre;
  g.save(); g.strokeStyle = HEDGE; g.lineWidth = 0.5 * metre; g.lineCap = 'butt';
  for (const w of m.walls) {
    for (let i = 0; i + 3 < w.p.length; i += 2) {
      const x1 = w.p[i], y1 = w.p[i + 1], x2 = w.p[i + 2], y2 = w.p[i + 3], L = Math.hypot(x2 - x1, y2 - y1);
      if (!L || Math.max(x1, x2) < rx0 - off || Math.min(x1, x2) > rx1 + off || Math.max(y1, y2) < ry0 - off || Math.min(y1, y2) > ry1 + off) continue;
      const nx = (-(y2 - y1) / L) * w.out * off, ny = ((x2 - x1) / L) * w.out * off; // into the compound
      g.beginPath(); g.moveTo(x1 + nx, y1 + ny); g.lineTo(x2 + nx, y2 + ny); g.stroke();
    }
  }
  g.restore();
}

/**
 * The map pre-rendered 1:1 in square chunks, made the first time they come into view and kept in a small cache.
 * One canvas for the whole map stops working past ~16k px a side (the district map is 17,200 x 15,440 px), so the
 * map is never drawn in one piece.
 */
export class TileLayer {
  static readonly CHUNK_TILES = 24;
  static readonly CACHE = 40; // chunks kept; 40 x 960^2 x 4 B is about 150 MB. Zoomed far out a screen can need more,
  private cap = TileLayer.CACHE; // so the cap grows to twice what is in view
  private cache = new Map<number, HTMLCanvasElement>();
  private roads: RoadDraw | null;
  /** chunks made this draw call, against the caller's budget */
  private made = 0;
  /**
   * `finish` paints over a fresh chunk (still translated to world px) after the tiles; `hole` is a world rectangle
   * [x0, y0, x1, y1] whose wholly covered chunks are never made or drawn (something else draws there).
   */
  constructor(readonly m: GameMap, private buildings: BuildingLayer | null = null,
    private finish: ((g: CanvasRenderingContext2D, rx0: number, ry0: number, rx1: number, ry1: number) => void) | null = null,
    private hole: [number, number, number, number] | null = null) { this.roads = prepareRoads(m); }

  private chunk(cx: number, cy: number, maxNew: number): HTMLCanvasElement | null {
    const key = cy * 65536 + cx;
    let c = this.cache.get(key);
    if (c) { this.cache.delete(key); this.cache.set(key, c); return c; } // most recently used goes last
    if (this.made >= maxNew) return null;
    this.made++;
    const m = this.m, n = TileLayer.CHUNK_TILES, px = n * m.tile;
    c = document.createElement('canvas'); c.width = px; c.height = px;
    const g = c.getContext('2d')!;
    g.translate(-cx * px, -cy * px);
    paintTiles(g, m, cx * n, cy * n, Math.min(m.w, (cx + 1) * n), Math.min(m.h, (cy + 1) * n), this.roads, this.buildings);
    this.finish?.(g, cx * px, cy * px, (cx + 1) * px, (cy + 1) * px);
    this.cache.set(key, c);
    if (this.cache.size > this.cap) this.cache.delete(this.cache.keys().next().value!);
    return c;
  }

  /** Draw the world rectangle (vx0, vy0, vw, vh) to the screen at zoom z, making at most `maxNew` new chunks. */
  draw(ctx: CanvasRenderingContext2D, vx0: number, vy0: number, vw: number, vh: number, z: number, maxNew = Infinity) {
    const m = this.m, px = TileLayer.CHUNK_TILES * m.tile, H = this.hole;
    this.made = 0;
    const cx0 = Math.max(0, Math.floor(vx0 / px)), cy0 = Math.max(0, Math.floor(vy0 / px));
    const cx1 = Math.min(Math.ceil(m.w / TileLayer.CHUNK_TILES) - 1, Math.floor((vx0 + vw) / px));
    const cy1 = Math.min(Math.ceil(m.h / TileLayer.CHUNK_TILES) - 1, Math.floor((vy0 + vh) / px));
    this.cap = Math.max(TileLayer.CACHE, 2 * (cx1 - cx0 + 1) * (cy1 - cy0 + 1));
    for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) {
      if (H && cx * px >= H[0] && cy * px >= H[1] && (cx + 1) * px <= H[2] && (cy + 1) * px <= H[3]) continue;
      const c = this.chunk(cx, cy, maxNew);
      if (!c) continue;
      // whole chunks, rounded to screen px so neighbours meet without hairline seams
      const x = Math.round((cx * px - vx0) * z), y = Math.round((cy * px - vy0) * z);
      const x2 = Math.round(((cx + 1) * px - vx0) * z), y2 = Math.round(((cy + 1) * px - vy0) * z);
      ctx.drawImage(c, x, y, x2 - x, y2 - y);
    }
  }
}

/** How the streets past the play map's edge are faded: the share of colour taken away, then a wash of newsprint over
 *  it, like the print running out at the sheet's edge. */
const SURROUND_GREY = 0.85, SURROUND_WASH = 'rgba(236,224,200,.5)';

/**
 * The streets past the play map's edge (<map>.surround.json, from tools/make-surround.mjs), so the world doesn't stop
 * at a hard square: drawn as scenery, greyed and washed out like a disabled control, never walked on. Everything static
 * is baked into the chunks once (tiles, roads, shadows, then the blocks and trees standing up, depth-sorted), so a frame
 * only copies images. The surround map's tile (inset, inset) is the play map's (0, 0).
 */
export class Surround {
  private tiles: TileLayer;
  private blocks: BuildingLayer | null;
  private trees: TreeLayer;
  /** world px of the play map's (0, 0) in the surround map */
  private readonly off: number;

  constructor(s: GameMap, inset: number, readonly play: GameMap) {
    const off = (this.off = inset * s.tile);
    this.blocks = BuildingLayer.from(s); this.trees = new TreeLayer(s);
    this.tiles = new TileLayer(s, this.blocks, (g, rx0, ry0, rx1, ry1) => {
      const far = -1e9; // no player out here: nothing fades
      this.trees.draw(g, rx0, ry0, rx1 - rx0, ry1 - ry0, far, far, this.blocks ? this.blocks.visible(rx0, ry0, rx1 - rx0, ry1 - ry0, far, far, 1) : []);
      g.save(); g.setTransform(1, 0, 0, 1, 0, 0);
      g.globalCompositeOperation = 'saturation'; g.globalAlpha = SURROUND_GREY; g.fillStyle = '#808080'; g.fillRect(0, 0, g.canvas.width, g.canvas.height);
      g.globalCompositeOperation = 'source-over'; g.globalAlpha = 1; g.fillStyle = SURROUND_WASH; g.fillRect(0, 0, g.canvas.width, g.canvas.height);
      g.restore();
    }, [off, off, off + play.width, off + play.height]);
  }

  /** Under the play map, in screen space like TileLayer.draw: two new chunks a frame at most, once the sprites are in. */
  draw(ctx: CanvasRenderingContext2D, vx0: number, vy0: number, vw: number, vh: number, z: number) {
    if (!this.trees.ready || (this.blocks && !this.blocks.ready)) return;
    this.tiles.draw(ctx, vx0 + this.off, vy0 + this.off, vw, vh, z, 2);
  }

  /** The play map's edge as a dashed ink line, a sheet's trim mark. World coordinates. */
  drawEdge(g: CanvasRenderingContext2D, z: number) {
    g.save(); g.strokeStyle = INK.asphalt; g.lineWidth = 2.5 / z; g.setLineDash([10 / z, 6 / z]); g.strokeRect(0, 0, this.play.width, this.play.height); g.restore();
  }
}

/** The border band just outside the map edge: metro-yellow stripes on asphalt, so the edge of the world is unmistakable. */
export function drawBorder(g: CanvasRenderingContext2D, m: GameMap, z: number) {
  if (!m.border) return;
  const b = m.border * m.tile;
  g.save();
  g.beginPath(); g.rect(-b, -b, m.width + 2 * b, m.height + 2 * b); g.rect(0, 0, m.width, m.height); g.clip('evenodd');
  g.fillStyle = INK.asphalt; g.fillRect(-b, -b, m.width + 2 * b, m.height + 2 * b);
  g.strokeStyle = INK.metro; g.lineWidth = b / 4;
  const step = b * 0.9;
  for (let k = -b; k < m.width + m.height + 3 * b; k += step) { g.beginPath(); g.moveTo(k, -b); g.lineTo(k - m.height - 2 * b, m.height + b); g.stroke(); }
  g.restore();
  g.strokeStyle = INK.metro; g.lineWidth = 3 / z; g.strokeRect(0, 0, m.width, m.height);
}

/** Which points are worth a marker, most first: the metro and the police, then help, then named places of note. */
/** groups whose unnamed points say nothing a walker can use: address nodes, and gates the tiles already show */
const QUIET = new Set(['other', 'place', 'gate', 'barrier']);
const POI_RANK: Record<string, number> = { metro: 0, police: 1, gate: 2, toilet: 3, water: 3, government: 4, heritage: 4, bus: 4, rail: 4, leisure: 5, place: 6, other: 7 };

/**
 * OSM point markers in the world rectangle: a dot per point (fixed screen size), and its name when it has one. Points
 * that say nothing a walker can use are left out: an unnamed point in a QUIET group (OSM address nodes, mostly)
 * and an `other` one zoomed out. The rest are placed by rank, and a point whose label would land on one already placed
 * is dropped, dot and all, so a lane of shops reads as a few names, not a pile.
 */
export function drawPois(g: CanvasRenderingContext2D, m: GameMap, vx0: number, vy0: number, vw: number, vh: number, z: number) {
  const r = 4.5 / z, pad = 120 / z, named = z >= 1.2, fs = 11 / z;
  const vis = m.pois
    .filter((p) => p.x > vx0 - pad && p.x < vx0 + vw + pad && p.y > vy0 - pad && p.y < vy0 + vh + pad)
    .filter((p) => (p.name || !QUIET.has(p.g)) && (named || p.g !== 'other'))
    .sort((a, b) => (POI_RANK[a.g] ?? 7) - (POI_RANK[b.g] ?? 7));
  g.font = `600 ${fs}px Inter,system-ui,sans-serif`;
  const taken: [number, number, number, number][] = [], seen = new Set<string>(), keep: { p: (typeof vis)[number]; label: boolean }[] = [];
  const hits = (q: [number, number, number, number]) => taken.some((t) => q[0] < t[2] && q[2] > t[0] && q[1] < t[3] && q[3] > t[1]);
  for (const p of vis) {
    if (p.name && seen.has(p.name)) continue; // one marker per name: a station's six "Chawri Bazaar" points say it once
    const label = named && !!p.name;
    const box: [number, number, number, number] = label
      ? [p.x - r, p.y - fs * 0.8, p.x + r + 3 / z + g.measureText(p.name!).width, p.y + fs * 0.6]
      : [p.x - r * 1.6, p.y - r * 1.6, p.x + r * 1.6, p.y + r * 1.6];
    if (hits(box)) continue;
    taken.push(box); keep.push({ p, label }); if (p.name) seen.add(p.name);
  }
  g.lineWidth = 1.5 / z; g.strokeStyle = 'rgba(0,0,0,.75)';
  for (const { p } of keep) { g.beginPath(); g.arc(p.x, p.y, r, 0, Math.PI * 2); g.fillStyle = POI_COLOR[p.g] || POI_COLOR.other; g.fill(); g.stroke(); }
  g.textAlign = 'left'; g.lineWidth = 3 / z; g.strokeStyle = INK.asphalt; g.fillStyle = INK.newsprint;
  for (const { p, label } of keep) if (label) { g.strokeText(p.name!, p.x + r + 3 / z, p.y + 4 / z); g.fillText(p.name!, p.x + r + 3 / z, p.y + 4 / z); }
}

interface Sprite { x: number; y: number; w: number; h: number; ax: number; ay: number; r: number; ppm?: number }

/** Something drawn standing up, depth-sorted by the y (world px) of its foot: further north is drawn first. */
export interface Standing { y: number; draw(g: CanvasRenderingContext2D): void }

// ------------------------------------------------------------------ buildings in 2.5D

/** The oblique view and dusk light the sprites were rendered with (tools/make-sprites.py), so blocks and trees agree. */
const VIEW_K: number = SPRITES.k ?? 0.6;
const SUN: number[] = SPRITES.light ?? [-0.458, 0.865, 0.204]; // towards the sun: (east, up, south)
const LEVEL_M = 3.2; // storey height, as the spec's buildings.levelHeightM
const SHADOW = 'rgb(46,34,92)', SHADOW_ALPHA = 0.3; // dusk-blue, not black

type RGB = [number, number, number];
const hex = (h: string): RGB => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)) as RGB;
const css = (c: RGB, k = 1, a = 1) => `rgba(${c.map((v) => Math.round(Math.min(255, v * k))).join(',')},${a})`;

/**
 * Looks per building style. concrete: the Brutalist pack's concrete (its base colour is a neutral grey) warmed for
 * dusk; plaster: the low-poly houses' palette (house_large5-8 walls, roofs and glass); plain: the cream and buff
 * plaster of Lutyens' Delhi; heritage: sandstone; yantra: Jantar Mantar's ochre plaster (--ochre).
 * A building picks one wall and one roof colour from its lists by a hash of its position, so streets don't repeat.
 */
const LOOKS: Record<string, { walls: string[]; roofs: string[]; glass: string | null; lit: number }> = {
  // concrete: the Brutalist pack's grey, warmed and lightened for dusk
  concrete: { walls: ['#B9B3BE', '#A9A7B8', '#C6BCB2', '#B0AAA0'], roofs: ['#8F8A99', '#9D958C', '#8A93A0'], glass: '#2C3550', lit: 0.35 },
  // plaster: the low-poly houses' walls (house_large5-8), brick and sand, with their slate and brown roofs lifted
  plaster: { walls: ['#C9875E', '#E2C2A2', '#D6A27E'], roofs: ['#7C5442', '#5E6B73', '#8E6450'], glass: '#3B6F8C', lit: 0.45 },
  // plain: Delhi's painted plaster, a pastel for every house (no saffron, and no green walls: see the brand red lines)
  plain: {
    walls: ['#E8A598', '#C9B6E4', '#9CC9E0', '#E9C46A', '#E9DCC6', '#D98C6A', '#8FC5D8', '#F1B5C8', '#B8A3D9', '#E3B88A'],
    roofs: ['#B9AEA4', '#C2876B', '#7E8BA3', '#A79C92', '#9B7E8E', '#C9A27E'], glass: '#2E3A56', lit: 0.3,
  },
  heritage: { walls: ['#C46A4F'], roofs: ['#A35540'], glass: null, lit: 0 },
  yantra: { walls: ['#E07A5A'], roofs: ['#F0A07F'], glass: null, lit: 0 },
  // shop: bright shopfronts along the main roads (generated, see the spec's shops section)
  shop: { walls: ['#F28C8C', '#7FC8F8', '#F7D774', '#B79CED', '#FF9F80', '#F4A6D7', '#9FB8F5'], roofs: ['#D8CFC2', '#C7BDB0', '#B9AFA2'], glass: '#3A2F4A', lit: 0.6 },
};
const SODIUM: RGB = [255, 181, 71];
/** shop awnings: two stripe colours each (no saffron with green, see the brand red lines) */
const AWNINGS: [string, string][] = [
  ['#FF6F59', '#F3E9D2'], ['#4FA3E0', '#F1ECE0'], ['#FFB547', '#2A2438'], ['#B79CED', '#F3E9D2'], ['#F4A6D7', '#F1ECE0'], ['#E0457B', '#F3E9D2'], ['#2F7DE1', '#FFB547'],
];

/**
 * Signboards (docs/plans/2026-10-17-bigger-and-signed.md): every building wears its name. Shops get a painted board over
 * the awning in the awning's colour; homes a cream name plate; offices a dark hoarding; heritage and the yantras a
 * sandstone plaque. rank: who keeps their board when boards would overlap (landmarks first).
 */
interface SignLook { panel: string; frame: string; text: string; rank: number }
const SIGNS: Record<string, SignLook> = {
  yantra: { panel: '#3B2620', frame: '#E07A5A', text: '#F7E3C8', rank: 5 },
  heritage: { panel: '#3B2620', frame: '#C46A4F', text: '#F7E3C8', rank: 4 },
  concrete: { panel: '#1E2638', frame: '#B9B3BE', text: '#F1ECE0', rank: 3 },
  plain: { panel: '#F1ECE0', frame: '#7A6658', text: '#2A2438', rank: 2 },
  plaster: { panel: '#F1ECE0', frame: '#7A6658', text: '#2A2438', rank: 2 },
};
const SIGN_UP_M = 3.7; // a shop's board sits on its front wall, just over the awning (2.95 m)

interface Wall { pts: number[]; c: string; my: number; len: number; front: boolean }
interface BDraw {
  /** rings in world px, outer first */
  rings: number[][];
  up: number; // screen lift of the roof, world px
  walls: Wall[]; // the south-facing ones, north first
  roof: string; rim: string; glass: string | null; lit: number; seed: number; floors: number;
  props: { x: number; y: number; s: Sprite; f: number }[];
  /** shops: the awning's two stripe colours; named buildings: the name and where its label sits (world px) */
  awning: [string, string] | null; name: string | null; lx: number; ly: number; shop: boolean;
  /** the signboard: where it is fixed (world px), flush on the shopfront or on legs over the roof, its look and rank */
  sign: { x: number; y: number; wall: boolean; look: SignLook; rank: number } | null;
  /** shops: the canopy over the pavement, as [x1, y1, x2, y2] of its wall edge and the reach (dx, dy), world px */
  canopy: { pts: number[]; dx: number; dy: number; south: boolean } | null;
  bb: [number, number, number, number]; // footprint, roof and shadow, world px
  foot: number; // y of the southmost footprint point, for depth sorting
}

/**
 * Every OSM footprint as a block of its real height, in the view the tree sprites use: walls facing south are seen,
 * the roof is lifted K * height up the screen, and the cast shadow (dusk light from the west) lies on the ground in
 * the cached tile chunks. Drawn as vectors each frame, sorted in with the trees; collision stays on the tiles.
 */
export class BuildingLayer {
  readonly list: BDraw[] = [];
  private img: HTMLImageElement | null = null;
  private scratch: HTMLCanvasElement | null = null;
  private readonly metre: number;
  /** world px of ground offset per world px of lift: where a roof point's shadow lands */
  private readonly sx: number; private readonly sy: number;

  static from(m: GameMap): BuildingLayer | null { return m.buildings?.length ? new BuildingLayer(m) : null; }
  /** the sprite atlas (roof props) has loaded */
  get ready() { return !!this.img; }

  private constructor(readonly m: GameMap) {
    const t = m.tile, metre = (this.metre = t / (m.mpt || 1));
    this.sx = -SUN[0] / SUN[1]; this.sy = -SUN[2] / SUN[1]; // metres east, south per metre of height
    const sprites = SPRITES.sprites as Record<string, Sprite>;
    for (const b of m.buildings || []) this.list.push(this.prepare(b, t, metre, sprites));
    const img = new Image();
    img.onload = () => { this.img = img; };
    img.src = SPRITES.image;
  }

  private prepare(b: Building, t: number, metre: number, sprites: Record<string, Sprite>): BDraw {
    const m = this.m, style = m.buildingStyles?.[b.s] || 'plain', look = LOOKS[style] || LOOKS.plain;
    const rings = [b.o, ...(b.i || [])].map((r) => r.map((v) => v * t));
    const o = rings[0], up = VIEW_K * b.h * metre;
    const seed = Math.abs(Math.round(o[0] * 7 + o[1] * 13)) % 9973;
    const wall = hex(look.walls[seed % look.walls.length]), roof = hex(look.roofs[Math.floor(seed / 7) % look.roofs.length]);
    // walls: an edge is seen when the solid's outward normal points south (down the screen)
    const walls: Wall[] = [];
    rings.forEach((r, ri) => {
      let area = 0;
      for (let i = 0, n = r.length / 2; i < n; i++) { const j = (i + 1) % n; area += r[2 * i] * r[2 * j + 1] - r[2 * j] * r[2 * i + 1]; }
      const flip = (area > 0) !== (ri === 0) ? -1 : 1; // holes face inwards
      for (let i = 0, n = r.length / 2; i < n; i++) {
        const j = (i + 1) % n, x1 = r[2 * i], y1 = r[2 * i + 1], x2 = r[2 * j], y2 = r[2 * j + 1];
        const len = Math.hypot(x2 - x1, y2 - y1);
        if (len < 1e-6) continue;
        const nx = (flip * (y2 - y1)) / len, ny = (flip * -(x2 - x1)) / len;
        if (ny <= 0.02) continue;
        const lam = Math.max(0, nx * SUN[0] + ny * SUN[2]);
        const front = !!b.f && nx * b.f[0] + ny * b.f[1] > 0.7; // a shop's wall that faces its road
        walls.push({ pts: [x1, y1, x2, y2, x2, y2 - up, x1, y1 - up], c: css(wall, 0.62 + 0.8 * lam), my: (y1 + y2) / 2, len: len / metre, front });
      }
    });
    walls.sort((a, c) => a.my - c.my);
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (let i = 0; i < o.length; i += 2) { x0 = Math.min(x0, o[i]); x1 = Math.max(x1, o[i]); y0 = Math.min(y0, o[i + 1]); y1 = Math.max(y1, o[i + 1]); }
    const foot = y1, sh = b.h * metre;
    const props = (b.p || []).flatMap(([px, py, k]) => {
      const s = sprites[m.roofKinds?.[k] || ''];
      return s ? [{ x: px * t, y: py * t - up, s, f: metre / (s.ppm || SPRITES.ppm) }] : [];
    });
    const awning = style === 'shop' ? AWNINGS[seed % AWNINGS.length] : null;
    const lx = o.filter((_, i) => i % 2 === 0).reduce((a, v) => a + v, 0) / (o.length / 2);
    const ly = o.filter((_, i) => i % 2 === 1).reduce((a, v) => a + v, 0) / (o.length / 2) - up;
    // the board: a shop's on the middle of its seen shopfront, anything else on legs over the middle of its roof
    let sign: BDraw['sign'] = null;
    if (b.n) {
      const look = awning ? { panel: css(hex(awning[0]), 0.42), frame: awning[0], text: '#FFF4D6', rank: 1 } : SIGNS[style] || SIGNS.plain;
      const fw = walls.filter((w) => w.front).sort((a, c) => c.len - a.len)[0];
      const lift = VIEW_K * Math.min(SIGN_UP_M, b.h - 0.4) * metre;
      sign = fw ? { x: (fw.pts[0] + fw.pts[2]) / 2, y: (fw.pts[1] + fw.pts[3]) / 2 - lift, wall: true, look, rank: look.rank } : { x: lx, y: ly, wall: false, look, rank: look.rank + Math.min(0.9, b.h / 60) };
    }
    return {
      awning, name: b.n || null, shop: style === 'shop', canopy: canopyOf(b, o, metre), lx, ly, sign,
      rings, up, walls, roof: style === 'shop' ? css(wall, 1.12) : css(roof, 0.95), rim: style === 'shop' ? css(wall, 1.3) : css(roof, 1.35), glass: look.glass, lit: look.lit, seed,
      floors: Math.max(1, Math.floor(b.h / LEVEL_M)), props,
      bb: [Math.min(x0, x0 + this.sx * sh), Math.min(y0 - up, y0 + this.sy * sh), Math.max(x1, x1 + this.sx * sh), Math.max(y1, y1 + this.sy * sh)],
      foot,
    };
  }

  /** Cast shadows over the world rectangle, into a tile chunk: one opaque mask, laid down once so overlaps don't stack. */
  paintShadows(g: CanvasRenderingContext2D, rx0: number, ry0: number, rx1: number, ry1: number) {
    const hit = this.list.filter((b) => b.bb[0] < rx1 && b.bb[2] > rx0 && b.bb[1] < ry1 && b.bb[3] > ry0);
    if (!hit.length) return;
    const w = Math.ceil(rx1 - rx0), h = Math.ceil(ry1 - ry0);
    const c = this.scratch && this.scratch.width === w && this.scratch.height === h ? this.scratch : (this.scratch = document.createElement('canvas'));
    c.width = w; c.height = h; // also clears it
    const s = c.getContext('2d')!;
    s.translate(-rx0, -ry0); s.fillStyle = SHADOW;
    for (const b of hit) {
      const dx = this.sx * b.up / VIEW_K, dy = this.sy * b.up / VIEW_K; // shadow offset of the roof: height times sx, sy
      // the swept footprint: every edge dragged along the shadow, plus the footprint at both ends. Each quad is filled
      // on its own: in one path, quads of opposite winding would cancel where they overlap
      const r = b.rings[0];
      for (let i = 0, n = r.length / 2; i < n; i++) {
        const j = (i + 1) % n;
        s.beginPath(); s.moveTo(r[2 * i], r[2 * i + 1]); s.lineTo(r[2 * j], r[2 * j + 1]); s.lineTo(r[2 * j] + dx, r[2 * j + 1] + dy); s.lineTo(r[2 * i] + dx, r[2 * i + 1] + dy); s.fill();
      }
      for (const [ox, oy] of [[0, 0], [dx, dy]]) { s.beginPath(); ring(s, b.rings[0], ox, oy); s.fill(); }
    }
    g.save(); g.globalAlpha = SHADOW_ALPHA; g.drawImage(c, rx0, ry0); g.restore();
  }

  /** The blocks in the world rectangle, as depth-sortable items; a block hiding the player at (px, py) fades. */
  visible(vx0: number, vy0: number, vw: number, vh: number, px: number, py: number, z: number): Standing[] {
    const out: Standing[] = [];
    const detail = z * this.metre >= 4; // windows only when a metre is at least 4 screen px
    for (const b of this.list) {
      const [x0, y0, x1, y1] = b.bb;
      if (x0 > vx0 + vw || x1 < vx0 || y0 > vy0 + vh || y1 < vy0) continue;
      out.push({ y: b.foot, draw: (g) => this.drawOne(g, b, detail, this.hides(b, px, py) ? 0.35 : 1) });
    }
    return out;
  }

  /** Is the point covered by the block on screen: somewhere between its footprint and its lifted roof? */
  private hides(b: BDraw, px: number, py: number): boolean {
    if (px < b.bb[0] || px > b.bb[2] || py < b.bb[1] || py > b.foot) return false;
    for (let k = 0; k <= 4; k++) if (inRing(b.rings[0], px, py + (b.up * k) / 4)) return true;
    return false;
  }

  private drawOne(g: CanvasRenderingContext2D, b: BDraw, detail: boolean, alpha: number) {
    const metre = this.metre;
    g.globalAlpha = alpha;
    // a canopy on the far (north) side goes first, so the shop's own walls and roof cover it
    if (b.canopy && !b.canopy.south) this.canopy(g, b);
    for (const w of b.walls) {
      g.fillStyle = w.c; g.beginPath(); g.moveTo(w.pts[0], w.pts[1]);
      for (let i = 2; i < 8; i += 2) g.lineTo(w.pts[i], w.pts[i + 1]);
      g.closePath(); g.fill();
      if (detail && w.front && b.awning) this.shopfront(g, b, w);
      else if (detail && b.glass && w.len > 2.5) this.windows(g, b, w);
    }
    g.beginPath();
    for (const r of b.rings) ring(g, r, 0, -b.up);
    g.fillStyle = b.roof; g.fill('evenodd');
    if (detail) { g.strokeStyle = b.rim; g.lineWidth = 0.5 * metre; g.lineJoin = 'round'; g.stroke(); } // the parapet
    if (b.canopy && b.canopy.south) this.canopy(g, b);
    if (this.img) for (const p of b.props) g.drawImage(this.img, p.s.x, p.s.y, p.s.w, p.s.h, p.x - p.s.ax * p.f, p.y - p.s.ay * p.f, p.s.w * p.f, p.s.h * p.f);
    g.globalAlpha = 1;
  }

  /** A shop's striped canopy over the pavement, 2.9 m up: stripes run from the wall out to the valance. */
  private canopy(g: CanvasRenderingContext2D, b: BDraw) {
    const c = b.canopy!, lift = VIEW_K * 2.9 * this.metre, [x1, y1, x2, y2] = c.pts;
    const mx1 = x1 + c.dx / 2, my1 = y1 + c.dy / 2 - lift, mx2 = x2 + c.dx / 2, my2 = y2 + c.dy / 2 - lift;
    g.save(); g.lineCap = 'butt';
    g.lineWidth = Math.hypot(c.dx, c.dy);
    g.strokeStyle = b.awning![1]; g.beginPath(); g.moveTo(mx1, my1); g.lineTo(mx2, my2); g.stroke();
    g.setLineDash([0.7 * this.metre, 0.7 * this.metre]); g.strokeStyle = b.awning![0]; g.stroke();
    // the valance: a darker edge where the canopy ends
    g.setLineDash([]); g.lineWidth = 0.25 * this.metre; g.strokeStyle = 'rgba(40,24,48,.45)';
    g.beginPath(); g.moveTo(x1 + c.dx, y1 + c.dy - lift); g.lineTo(x2 + c.dx, y2 + c.dy - lift); g.stroke();
    g.restore();
  }

  /** A shop's face: a rolled-up shutter with the lit shop inside, a striped awning above it, windows over that. */
  private shopfront(g: CanvasRenderingContext2D, b: BDraw, w: Wall) {
    const metre = this.metre, k = VIEW_K * metre, [x1, y1, x2, y2] = w.pts, ins = 0.4 / Math.max(w.len, 0.8);
    const ax = x1 + (x2 - x1) * ins, ay = y1 + (y2 - y1) * ins, bx = x2 - (x2 - x1) * ins, by = y2 - (y2 - y1) * ins;
    g.save();
    // the open front: warm light inside, the shutter rolled up at the top
    g.fillStyle = css(SODIUM, 0.85, 0.9);
    g.beginPath(); g.moveTo(ax, ay); g.lineTo(bx, by); g.lineTo(bx, by - 2.3 * k); g.lineTo(ax, ay - 2.3 * k); g.closePath(); g.fill();
    g.strokeStyle = '#8C8F99'; g.lineWidth = 0.35 * k; g.beginPath(); g.moveTo(ax, ay - 2.45 * k); g.lineTo(bx, by - 2.45 * k); g.stroke();
    // awning: two stripe colours along the front
    g.lineCap = 'butt'; g.lineWidth = 0.75 * k;
    g.strokeStyle = b.awning![1]; g.beginPath(); g.moveTo(x1, y1 - 2.95 * k); g.lineTo(x2, y2 - 2.95 * k); g.stroke();
    g.setLineDash([0.6 * metre, 0.6 * metre]); g.strokeStyle = b.awning![0]; g.stroke();
    g.restore();
    if (b.floors > 1 && b.glass) this.windows(g, { ...b, floors: b.floors - 1 }, { ...w, pts: [x1, y1 - LEVEL_M * k, x2, y2 - LEVEL_M * k, ...w.pts.slice(4)] });
  }

  private textW = new Map<BDraw, number>(); // a name's width at a 100 px font, measured once

  /**
   * Every building's signboard, drawn after everything standing so it stays readable: a framed board with the name,
   * flush on a shopfront or on two legs over a roof. Boards are screen-sized, growing with the zoom between 10 and 17 px
   * text; landmarks show once a metre is 3 screen px, everything else at 5. Where boards would overlap, the higher
   * rank keeps its board, then the one nearer the player at (px, py); a board over the player fades.
   */
  labels(g: CanvasRenderingContext2D, vx0: number, vy0: number, vw: number, vh: number, z: number, px = NaN, py = NaN) {
    const ppm = z * this.metre;
    if (ppm < 3) return;
    const pad = 300 / z, cands: { b: BDraw; d: number }[] = [];
    for (const b of this.list) {
      const s = b.sign;
      if (!s || (s.rank < 3 && ppm < 5)) continue;
      if (s.x < vx0 - pad || s.x > vx0 + vw + pad || s.y < vy0 - pad / 3 || s.y > vy0 + vh + pad / 3) continue;
      cands.push({ b, d: Number.isNaN(px) ? 0 : Math.hypot(s.x - px, s.y - py) });
    }
    cands.sort((a, c) => c.b.sign!.rank - a.b.sign!.rank || a.d - c.d);
    g.save(); g.textAlign = 'center'; g.textBaseline = 'middle'; g.lineJoin = 'round';
    const placed: [number, number, number, number][] = [], gap = 3 / z, head = 6 * this.metre; // a tall body, as drawn (3.2x)
    for (const { b } of cands) {
      if (placed.length >= 90) break;
      const s = b.sign!, name = b.name!;
      let w100 = this.textW.get(b);
      if (w100 === undefined) { g.font = '700 100px Mukta,system-ui,sans-serif'; this.textW.set(b, (w100 = g.measureText(name).width)); }
      let fs = Math.max(10, Math.min(15, ppm * 1.1)) + (s.rank >= 3 ? 2 : 0);
      fs = Math.min(fs, (230 * 100) / w100); // long names get a smaller hand, not a wider board
      const W = (w100 * fs) / 100 + 1.2 * fs, H = 1.5 * fs, legs = s.wall ? 0 : 0.9 * fs; // screen px
      const bw = W / z, bh = H / z, x0 = s.x - bw / 2, y1 = s.y - legs / z, y0 = y1 - bh;
      if (placed.some(([a0, b0, a1, b1]) => x0 < a1 + gap && x0 + bw > a0 - gap && y0 < b1 + gap && y1 > b0 - gap)) continue;
      placed.push([x0, y0, x0 + bw, y1]);
      g.globalAlpha = px > x0 && px < x0 + bw && py - head < y1 && py > y0 ? 0.35 : 1;
      const lw = Math.max(1.5, 0.14 * fs) / z;
      if (!s.wall) { // the hoarding's legs, from the roof up to the board
        g.strokeStyle = '#3A3440'; g.lineWidth = lw * 1.2;
        g.beginPath(); for (const k of [-0.3, 0.3]) { g.moveTo(s.x + k * bw, s.y); g.lineTo(s.x + k * bw, y1); } g.stroke();
      }
      g.fillStyle = 'rgba(8,10,16,.3)'; g.fillRect(x0 + lw, y0 + lw * 1.5, bw, bh); // a soft drop shadow
      g.fillStyle = s.look.panel; g.fillRect(x0, y0, bw, bh);
      g.strokeStyle = s.look.frame; g.lineWidth = lw; g.strokeRect(x0 + lw / 2, y0 + lw / 2, bw - lw, bh - lw);
      g.font = `700 ${fs / z}px Mukta,system-ui,sans-serif`; g.fillStyle = s.look.text;
      g.fillText(name, s.x, (y0 + y1) / 2 + 0.06 * bh);
    }
    g.restore();
  }

  /** A band of windows per storey, dark glass with a few lit by the lamps inside (it is dusk). */
  private windows(g: CanvasRenderingContext2D, b: BDraw, w: Wall) {
    const metre = this.metre, k = VIEW_K * metre, [x1, y1, x2, y2] = w.pts;
    g.save(); g.lineCap = 'butt'; g.lineWidth = 1.3 * k;
    for (let f = 0; f < b.floors; f++) {
      const lift = (f * LEVEL_M + 1.7) * k;
      g.beginPath(); g.moveTo(x1, y1 - lift); g.lineTo(x2, y2 - lift);
      g.setLineDash([1.4 * metre, 1.6 * metre]); g.lineDashOffset = ((b.seed + f) % 3) * metre; g.strokeStyle = b.glass!; g.stroke();
      if (b.lit > 0 && ((b.seed >> f) & 3) < b.lit * 4) {
        g.setLineDash([1.4 * metre, 7.6 * metre]); g.strokeStyle = css(SODIUM, 1, 0.55); g.stroke();
      }
    }
    g.restore();
  }
}

/** A shop's canopy: on the ring edge that faces its road (b.f), reaching 1.8 m out over the pavement. */
function canopyOf(b: Building, o: number[], metre: number): BDraw['canopy'] {
  if (!b.f) return null;
  let best = -1, at = -1;
  const n = o.length / 2;
  let area = 0;
  for (let i = 0; i < n; i++) { const j = (i + 1) % n; area += o[2 * i] * o[2 * j + 1] - o[2 * j] * o[2 * i + 1]; }
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n, ex = o[2 * j] - o[2 * i], ey = o[2 * j + 1] - o[2 * i + 1], l = Math.hypot(ex, ey) || 1;
    const nx = (area > 0 ? ey : -ey) / l, ny = (area > 0 ? -ex : ex) / l, d = nx * b.f[0] + ny * b.f[1];
    if (d > best) { best = d; at = i; }
  }
  if (best < 0.7) return null;
  const j = (at + 1) % n, reach = 1.8 * metre;
  return { pts: [o[2 * at], o[2 * at + 1], o[2 * j], o[2 * j + 1]], dx: b.f[0] * reach, dy: b.f[1] * reach, south: b.f[1] > 0 };
}

/** Adds a ring (flat world px pairs) to the current path, moved by (ox, oy). */
function ring(g: CanvasRenderingContext2D, r: number[], ox: number, oy: number) {
  g.moveTo(r[0] + ox, r[1] + oy);
  for (let i = 2; i < r.length; i += 2) g.lineTo(r[i] + ox, r[i + 1] + oy);
  g.closePath();
}

function inRing(r: number[], x: number, y: number): boolean {
  let inside = false;
  for (let i = 0, n = r.length / 2, j = n - 1; i < n; j = i++) {
    const xi = r[2 * i], yi = r[2 * i + 1], xj = r[2 * j], yj = r[2 * j + 1];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/**
 * Trees and bushes from the map's trees list, drawn from the sprite atlas (tools/make-sprites.py) above the people,
 * bucketed by chunk so a frame only looks at the few hundred near the screen. A canopy you stand under fades, so your
 * own body never disappears; everyone else's can hide under the trees.
 */
/** The blue of the sign modelled on the metro entrance (tools/make-sprites.py BLUE_SIGN), and its lettering. */
export const METRO_SIGN = { fill: '#2452A0', ink: '#FFFFFF' } as const;
/**
 * Sign sizes in metres as drawn: English and Hindi type, padding. It stands on the entrance's canopy, roofM above the
 * ground at life size (tools/make-sprites.py, the gate's H), lifted up the screen as the sprites draw heights.
 */
const SIGN_M = { en: 2.6, hi: 2.4, pad: 0.9, roofM: 3.3 };

/**
 * The station's nameplate over its entrance: English over Hindi, white on the entrance's blue, like the sign on the
 * model, which is left blank for this. Drawn as text every frame, never baked into a sprite. (cx, bottom) is the
 * plate's bottom middle, world px; `metre` world px per metre. No network's logo: the plate carries the name only.
 */
export function drawStationSign(g: CanvasRenderingContext2D, en: string, hi: string | undefined, cx: number, bottom: number, metre: number) {
  const en_ = SIGN_M.en * metre, hi_ = SIGN_M.hi * metre, pad = SIGN_M.pad * metre;
  g.save();
  g.font = FONT.body(en_, 700); const wEn = g.measureText(en).width;
  let wHi = 0;
  if (hi) { g.font = FONT.hi(hi_, 600); wHi = g.measureText(hi).width; }
  const w = Math.max(wEn, wHi) + pad * 2, h = en_ * 1.25 + (hi ? hi_ * 1.3 : 0) + pad * 1.4, x = cx - w / 2, y = bottom - h;
  g.fillStyle = INK.asphalt; g.fillRect(x + pad * 0.3, y + pad * 0.3, w, h); // the brand's hard shadow
  g.fillStyle = METRO_SIGN.fill; g.fillRect(x, y, w, h);
  g.strokeStyle = METRO_SIGN.ink; g.lineWidth = Math.max(1, metre * 0.18); g.strokeRect(x + pad * 0.35, y + pad * 0.35, w - pad * 0.7, h - pad * 0.7);
  g.fillStyle = METRO_SIGN.ink; g.textAlign = 'center'; g.textBaseline = 'alphabetic';
  g.font = FONT.body(en_, 700); g.fillText(en, cx, y + pad * 0.7 + en_ * 1.0);
  if (hi) { g.font = FONT.hi(hi_, 600); g.fillText(hi, cx, y + pad * 0.7 + en_ * 1.25 + hi_ * 1.0); }
  g.restore();
}

export class TreeLayer {
  private buckets = new Map<number, { x: number; y: number; s: Sprite; f: number; sign?: { n: string; hi?: string; lift: number } }[]>();
  private img: HTMLImageElement | null = null;
  private empty = true;
  private readonly size: number;
  /** the sprite atlas has loaded, or there is nothing to draw from it */
  get ready() { return this.empty || !!this.img; }
  constructor(readonly m: GameMap) {
    this.size = TileLayer.CHUNK_TILES * m.tile;
    const metre = m.tile / (m.mpt || 1); // world px per metre
    const sprites = SPRITES.sprites as Record<string, Sprite>;
    // traffic signals and the metro entrances (with the station's name for their sign) ride along: same atlas, same
    // depth sort
    const items: [number, number, string, number, { n: string; hi?: string }?][] = [
      ...(m.trees || []).map(([x, y, k, s]): [number, number, string, number] => [x, y, m.treeKinds?.[k] || '', s]),
      ...(m.signals || []).map(([x, y]): [number, number, string, number] => [x, y, 'traffic_light1', m.markings?.signals.scale || 1]),
      ...(m.stations || []).map((st): [number, number, string, number, { n: string; hi?: string }?] => [st.x, st.y, st.k, st.s, st.n ? { n: st.n, hi: st.hi } : undefined]),
    ];
    if (!items.length) return;
    for (const [tx, ty, kind, scale, sign] of items) {
      const s = sprites[kind];
      if (!s) continue;
      const x = tx * m.tile, y = ty * m.tile, key = Math.floor(y / this.size) * 65536 + Math.floor(x / this.size);
      let b = this.buckets.get(key); if (!b) this.buckets.set(key, (b = []));
      b.push({ x, y, s, f: (scale * metre) / (s.ppm || SPRITES.ppm), ...(sign && { sign: { ...sign, lift: SIGN_M.roofM * scale * VIEW_K * metre } }) });
    }
    for (const b of this.buckets.values()) b.sort((p, q) => p.y - q.y);
    this.empty = false;
    const img = new Image();
    img.onload = () => { this.img = img; };
    img.src = SPRITES.image;
  }

  /**
   * Draw in world coordinates (the caller has applied the camera); (px, py) is the local player. `extra` are other
   * things standing up (buildings), sorted in with the trees by the y of their foot.
   */
  draw(g: CanvasRenderingContext2D, vx0: number, vy0: number, vw: number, vh: number, px: number, py: number, extra: Standing[] = []) {
    if (!this.img) { for (const e of [...extra].sort((p, q) => p.y - q.y)) e.draw(g); return; }
    const pad = 4 * this.m.tile; // a sprite reaches at most a few tiles from its trunk
    const cx0 = Math.floor((vx0 - pad) / this.size), cx1 = Math.floor((vx0 + vw + pad) / this.size);
    const cy0 = Math.floor((vy0 - pad) / this.size), cy1 = Math.floor((vy0 + vh + pad) / this.size);
    const vis = [];
    for (let cy = cy0; cy <= cy1; cy++) for (let cx = cx0; cx <= cx1; cx++) {
      for (const t of this.buckets.get(cy * 65536 + cx) || []) {
        const x = t.x - t.s.ax * t.f, y = t.y - t.s.ay * t.f;
        if (x < vx0 + vw && x + t.s.w * t.f > vx0 && y < vy0 + vh && y + t.s.h * t.f > vy0) vis.push(t);
      }
    }
    const img = this.img, metre = this.m.tile / (this.m.mpt || 1);
    const all: Standing[] = vis.map((t) => ({ y: t.y, draw: (g) => {
      // the canopy sits up-screen of the trunk; fade it when the player is inside that disc
      const cr = t.s.r * t.f * (t.s.ppm || SPRITES.ppm) * 1.3, cyc = t.y - cr * 1.2;
      g.globalAlpha = Math.hypot(px - t.x, py - cyc) < cr * 1.4 ? 0.35 : 1;
      g.drawImage(img, t.s.x, t.s.y, t.s.w, t.s.h, t.x - t.s.ax * t.f, t.y - t.s.ay * t.f, t.s.w * t.f, t.s.h * t.f);
      if (t.sign) { g.globalAlpha = 1; drawStationSign(g, t.sign.n, t.sign.hi, t.x, t.y - t.sign.lift, metre); }
    } }));
    all.push(...extra);
    all.sort((p, q) => p.y - q.y);
    for (const t of all) t.draw(g);
    g.globalAlpha = 1;
  }
}

/**
 * type: 0 protester, 1 police, 2 npc, 4 friend (story mode; k is their place in the toli). For an npc, k is 1 for the
 * stage's crowd and 0 for a passer-by. poster: what the local player carries from the bazaar (null: nothing).
 */
export interface DrawEnt { id: number; type: number; x: number; y: number; face: number; k?: number; poster?: string | null }

export function drawEnt(g: CanvasRenderingContext2D, e: DrawEnt, r: number, isMe: boolean) {
  g.beginPath(); g.arc(e.x, e.y, r, 0, Math.PI * 2);
  g.fillStyle = TEAM_COLOR[e.type] || '#999'; g.fill();
  const k = r / 12; // line widths were tuned for 12 px bodies; scale them with the body
  g.lineWidth = (isMe ? 3 : 1.5) * k; g.strokeStyle = isMe ? '#fff' : 'rgba(0,0,0,.55)'; g.stroke();
  // facing nub
  g.beginPath(); g.moveTo(e.x + Math.cos(e.face) * r * 0.2, e.y + Math.sin(e.face) * r * 0.2);
  g.lineTo(e.x + Math.cos(e.face) * r * 1.0, e.y + Math.sin(e.face) * r * 1.0);
  g.lineWidth = 2.5 * k; g.strokeStyle = 'rgba(0,0,0,.6)'; g.stroke();
}
